import React, { useState, useEffect, useRef } from "react";
import {
  Button, Card, CardHeader, CardBody, Row, Col,
  Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, TabContent, TabPane, Table
} from "reactstrap";
import { useParams } from "react-router-dom";
import { URL } from "../../api";
import axiosInstance from "../../Request";
import PaymentStatementOfAccount from "../Details/PaymentStatementOfAccount";
import PaymentVerificationReport from "../Details/PaymentVerificationReport";
import PDF_ICON from "../../assets/img/pdf_icon.png";
import saloraLogo from "../../assets/img/salora_logo.jpg";
import html2pdf from "html2pdf.js"; 


const TABS = [
  { id: "agreement", label: "Agreement" },
  { id: "key_fact_statement", label: "KFS" },
  { id: "noc", label: "NOC" },
  { id: "consents", label: "Consents" },
  { id: "statement_of_account", label: "SOA" },
  { id: "sanction_letter", label: "Sanction Letter" },
  { id: "verification_report", label: "Verification Report" },
  { id: "loan_card_report", label: "Loan Card Report" },

];

const Legal = () => {
  const { cid: customer_id, lid: lead_id } = useParams();
  
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [documentUrl, setDocumentUrl] = useState(null);
  // All matching docs for the active tab (e.g. original + amended -AMD set) so both are shown.
  const [docList, setDocList] = useState([]);
  const [consents, setConsents] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
   const [loanCardData, setLoanCardData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loanIdFilter, setLoanIdFilter] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState("");
  const [statementFullScreen, setStatementFullScreen] = useState(false);

   const activeTab = TABS[activeTabIndex];
   const pdfRef = useRef();

  // Loads the loan-card statement for a given loan (lead) id. Used both on tab open (the page's
  // lead_id) and by the Loan ID filter on the Loan Card Report tab (an entered loan id).
  const loadLoanCard = async (leadIdToUse) => {
    if (!leadIdToUse || !customer_id) return;
    setLoading(true);
    setError(null);
    setLoanCardData([]);
    try {
      const res = await axiosInstance.get(
        `${URL.LOAN_STATEMENT}?lead_id=${leadIdToUse}&customer_id=${customer_id}`
      );
      const d = res.data;
      setLoanCardData([{
        loanStatement: {
          date: new Date().toLocaleDateString("en-GB"),
          name: d.NAME,
          address: d.ADDRESS,
          loanNumber: d.LOAN_NUMBER,
          phone: d.PHONE
        },
        loanDetails: {
          agreementDate: d.LOAN_DETAILS.AGREEMENT_DATE,
          emiAmount: d.LOAN_DETAILS.EMI_AMOUNT,
          financeAmount: d.LOAN_DETAILS.FINANCE_AMOUNT,
          tenure: d.LOAN_DETAILS.TENURE,
          installments: d.INSTALLMENT_TABLE.map((e, i) => ({
            no: i + 1,
            emiDue: e.EMI_DUE,
            dueDate: e.DUE_DATE,
            paid: e.EMI_PAID,
            receipt: e.RECEIPT_DATE,
            chargesDue: e.CHARGES_DUE,
            chargesPaid: e.CHARGES_PAID,
            discount: "0.00"
          }))
        },
        ledgerDetails: d.LEDGER.map(l => ({
          date: l.DATE,
          desc: l.DESCRIPTION,
          mode: l.MODE,
          instrument: l.INSTRUMENT_NO,
          bank: "-",
          amount: l.AMOUNT
        }))
      }]);
    } catch (e) {
      setError("Failed to load loan card for the given Loan ID.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentUrl = async (documentName) => {
    if (!customer_id || !lead_id) return;

    setLoading(true);
    setError(null);
    setDocumentUrl(null);
    setDocList([]);
    setConsents([]);
    setLoanCardData([]);
    

    try {
      if (documentName === "consents") {
        const response = await axiosInstance.get(
          `${URL.LEGAL_CONSENT}?customer_id=${customer_id}&lead_id=${lead_id}&document=${documentName}`
        );
        setConsents(response.data || []);
        return;

        
      }
// SOA renders a live detailed Statement of Account component (charges / collection /
// outstanding / ledger), not a stored PDF — so skip the document lookup.
if (documentName === "statement_of_account" || documentName === "verification_report") {
  setError(null);
  setLoading(false);
  return;
}
if (documentName === "loan_card_report") {
  await loadLoanCard(loanIdFilter.trim() || lead_id);
  return;
}


      // if (["agreement", "key_fact_statement"].includes(documentName)) {
      //   const response = await axiosInstance.get(`${URL.AGREEMENT}?leadId=${lead_id}`);
      //   const docs = response.data.agreement_documents || [];

      //   const matchedDoc = docs.find(doc => {
      //     if (documentName === "agreement") {
      //       return doc.document_name.toLowerCase().includes("loan_agreement");
      //     }
      //     if (documentName === "key_fact_statement") {
      //       return doc.document_name.toLowerCase().includes("keyfactstatement");
      //     }
      //     return false;
      //   });

      //   if (matchedDoc) {
      //     setDocumentUrl(matchedDoc.document_path);
      //   } else {
      //     setError("Document not found.");
      //   }
      //   return;
      // }


      if (["agreement", "key_fact_statement"].includes(documentName)) {
    const response = await axiosInstance.get(`${URL.AGREEMENT}?leadId=${lead_id}`);
    const docs = response.data.agreement_documents || [];

    // Return ALL matching docs (original + any -AMD amended set), amended first, so both are shown.
    const matched = docs.filter(doc => {
        const fileName = doc.document_name.toLowerCase();
        if (documentName === "agreement") {
            return fileName.includes("loan_agreement");
        }
        if (documentName === "key_fact_statement") {
            return fileName.includes("keyfactstatement") || fileName.includes("kfs");
        }
        return false;
    }).map(doc => ({
        ...doc,
        amended: doc.document_name.toLowerCase().includes("-amd-"),
    })).sort((a, b) => (b.amended === a.amended ? 0 : b.amended ? 1 : -1));

    if (matched.length) {
        setDocList(matched);
        setDocumentUrl(matched[0].document_path);
    } else {
        setError("Document not found.");
    }
    return;
}

      const response = await axiosInstance.get(
        `${URL.LEGAL_DOCUMENT}?customer_id=${customer_id}&lead_id=${lead_id}&document=${documentName}`
      );

      if (response.status === 404 || !response.data?.url) {
        setError("Document not found.");
      } else {
        setDocumentUrl(response.data.url);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load document.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentUrl(activeTab.id);
  }, [activeTabIndex, customer_id, lead_id]);

  // Force the backend to re-render the three payment-flow PDFs (agreement, KFS, sanction letter)
  // so corrected figures (processing fee, GST, APR) replace the cached documents. The application id
  // follows the SC<customerId><leadId> pattern used across the payment flow.
  const regenerateDocuments = async () => {
    setRegenerating(true);
    setRegenMsg("");
    try {
      const applicationId = `SC${customer_id}${lead_id}`;
      await axiosInstance.post(URL.PAYMENT_AGREEMENT_GENERATE, { applicationId });
      setRegenMsg("Documents regenerated. Reloading…");
      await fetchDocumentUrl(activeTab.id);
    } catch (err) {
      console.error(err);
      setRegenMsg("Failed to regenerate documents.");
    } finally {
      setRegenerating(false);
    }
  };

  const toggleTabByIndex = (index) => {
    if (index !== activeTabIndex) {
      setActiveTabIndex(index);
      setError(null);
    }
  };

  const filteredInstallments = loanCardData.length > 0
  ? loanCardData[0].loanDetails.installments.filter((inst) => {
      if (!inst.dueDate) return false; // ✅ NULL guard

      const instDate = new Date(
        inst.dueDate.split("-").reverse().join("-")
      );

      return (!fromDate || new Date(fromDate) <= instDate) &&
             (!toDate || new Date(toDate) >= instDate);
    })
  : [];


  

const filteredLedgerDetails = loanCardData.length > 0
  ? loanCardData[0].ledgerDetails.filter((ld) => {
      if (!ld.date) return false; // ✅ NULL guard

      const ledgerDate = new Date(
        ld.date.split("-").reverse().join("-")
      );

      return (!fromDate || new Date(fromDate) <= ledgerDate) &&
             (!toDate || new Date(toDate) >= ledgerDate);
    })
  : [];

  const openModal = () => setModal(true);
  const closeModal = () => setModal(false);


  const generatePDF = () => {
  const element = pdfRef.current;
  const opt = {
    margin: [1.2, 0.5, 1.2, 0.5],
    filename: 'Loan_Statement.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }, // 👈 CHANGED TO LANDSCAPE
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  setTimeout(() => {
    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get('pdf')
      .then(async (pdf) => {
        const pages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const logoImage = await fetch(saloraLogo)
          .then((r) => r.blob())
          .then((blob) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            })
          );

        for (let i = 1; i <= pages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(12);
          pdf.setTextColor(0);
          pdf.setFont(undefined, 'bold');
          pdf.addImage(logoImage, 'JPEG', pageWidth - 1.8, 0.4, 1.2, 0.5);

       const y = pageHeight - 1.1;
pdf.setFontSize(9);

// Company Name
pdf.setFont(undefined, 'bold');
pdf.text('Salora Capital Limited', pageWidth / 2, y, { align: 'center' });

// Register Address (Line 1)
pdf.setFont(undefined, 'normal');
pdf.text(
  'Register Address: 1st Floor, Commerz II, CTS No 95 4 B 3 & 4 590, Oberoi Garden City,',
  pageWidth / 2,
  y + 0.25,
  { align: 'center' }
);

// Register Address (Line 2)
pdf.text(
  'Off Western Express Highway, Goregaon East, Mumbai, Maharashtra, India, 400063',
  pageWidth / 2,
  y + 0.4,
  { align: 'center' }
);

// Phone + Email (same line)
pdf.text(
  'Phone: 9289119401, Email: connect@saloracapital.com',
  pageWidth / 2,
  y + 0.55,
  { align: 'center' }
);

        }
      })
      .save();
  }, 300);
};


  return (
    <>
      {/* MODAL PREVIEW */}
      <Modal isOpen={modal} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>Document Preview</ModalHeader>
        <ModalBody style={{ height: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          {documentUrl ? (
            <iframe
              src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(documentUrl)}`}
              width="100%"
              height="500px"
              style={{ width: "100%", height: "100%", border: "none" }}
              sandbox="allow-scripts allow-same-origin"
            />

          ) : (
            <p>No preview available</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>Close</Button>
        </ModalFooter>
      </Modal>

 <Card className="bg-secondary shadow mb-4">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col xs="6"><h3 className="mb-0">Legal Documents</h3></Col>
            <Col xs="6" className="text-end">
              {/* Regenerate Docs button hidden */}
              <Button color="primary" size="sm" onClick={() => toggleTabByIndex((activeTabIndex - 1 + TABS.length) % TABS.length)}>Previous</Button>{' '}
              <Button color="primary" size="sm" onClick={() => toggleTabByIndex((activeTabIndex + 1) % TABS.length)}>Next</Button>
              {regenMsg && <div className="small text-muted mt-1">{regenMsg}</div>}
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Nav tabs>
            {TABS.map((tab, idx) => (
              <NavItem key={tab.id}>
                <NavLink className={activeTabIndex === idx ? "active" : ""} onClick={() => toggleTabByIndex(idx)} style={{ cursor: "pointer" }}>
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab.id} style={{ marginTop: "1rem" }}>
  <TabPane tabId={activeTab.id}>
    {loading && <Spinner color="primary" />}
    {error && <p className="text-danger">{error}</p>}

              {/* Loan Card Report PDF Download Button */}
              {!loading && !error && activeTab.id === "loan_card_report" && loanCardData.length > 0 && (
                <>
                  <div className="text-end mb-3">
                    <Button color="info" className="me-2" onClick={() => setStatementFullScreen(true)}>View Full Screen</Button>
                    <Button color="danger" onClick={generatePDF}>Download PDF</Button>
                  </div>

                   <div className="d-flex justify-content-end align-items-center mb-3">
                      <label className="me-2">Loan ID:</label>
                      <input type="text" value={loanIdFilter} placeholder="Loan ID"
                             onChange={(e) => setLoanIdFilter(e.target.value)}
                             onKeyDown={(e) => { if (e.key === "Enter") loadLoanCard(loanIdFilter.trim() || lead_id); }}
                             className="form-control me-3" style={{ width: "160px" }} />
                      <label className="me-2">From:</label>
                      <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control me-3" style={{ width: "180px" }} />
                      <label className="me-2">To:</label>
                      <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control me-3" style={{ width: "180px" }} />
                      <Button color="primary" className="me-2" onClick={() => loadLoanCard(loanIdFilter.trim() || lead_id)}>Search</Button>
                      <Button color="secondary" outline onClick={() => { setLoanIdFilter(""); setFromDate(""); setToDate(""); loadLoanCard(lead_id); }}>Reset</Button>
                    </div>


                  {/* Report Content to Download */}
                 {statementFullScreen && (
                   <Button color="secondary" size="sm"
                           style={{ position: "fixed", top: "16px", right: "24px", zIndex: 2001 }}
                           onClick={() => setStatementFullScreen(false)}>✕ Close</Button>
                 )}
                 <div style={statementFullScreen ? { position: "fixed", inset: 0, zIndex: 2000, background: "linear-gradient(135deg,#1f2d4d,#33507e)", overflow: "auto", padding: "48px 0" } : { display: "contents" }}>
                 <div ref={pdfRef} className="pdf-container fs-statement"
                      style={statementFullScreen ? { maxWidth: "940px", margin: "0 auto", background: "#fff", padding: "0 0 24px", boxShadow: "0 10px 40px rgba(0,0,0,0.35)", borderRadius: "10px", overflow: "hidden" } : undefined}>
  
                   

                    {/* Loan Summary */}
                    <Card className="mb-4">
                      <CardHeader><h4 className="mb-0 text-center">Loan Statement</h4></CardHeader>
                      <CardBody>
                        <Table bordered striped>
                          <tbody>
                            <tr><th style={{ width: "220px" }}>Name</th><td>{loanCardData[0].loanStatement.name}</td></tr>
                            <tr><th style={{ width: "220px" }}>Address</th><td>{loanCardData[0].loanStatement.address}</td></tr>
                            <tr><th style={{ width: "220px" }}>Loan Number</th><td>{loanCardData[0].loanStatement.loanNumber}</td></tr>
                            <tr><th style={{ width: "220px" }}>Phone</th><td>{loanCardData[0].loanStatement.phone}</td></tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>

                    {/* Loan Details */}
                    <Card className="mb-4 loancard">
                      <CardHeader><h4 className="mb-0">Loan Details</h4></CardHeader>
                      <CardBody>
                        <Table bordered striped>
                          <thead>
                            <tr><th>Agreement Date</th><th>EMI Amount</th><th>Finance Amount</th><th>Tenure <br /> (In Days)</th></tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{loanCardData[0].loanDetails.agreementDate}</td>
                              <td>{loanCardData[0].loanDetails.emiAmount}</td>
                              <td>{loanCardData[0].loanDetails.financeAmount}</td>
                              <td>{loanCardData[0].loanDetails.tenure}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>

                    {/* Installments */}
                    <Card className="mb-4">
                      <CardHeader><h4 className="mb-0">Installments</h4></CardHeader>
                      <CardBody>
                        <Table bordered striped>
                          <thead>
                            <tr><th>No.</th><th>EMI Due</th><th>Due Date</th><th>EMI Paid</th><th>Receipt Date</th><th>Charges Due</th><th>Charges Paid</th>
                            {/* <th>Discount</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredInstallments.length > 0 ? filteredInstallments.map((inst, idx) => (
                              <tr key={idx}>
                                <td>{inst.no}</td>
                                <td>{inst.emiDue}</td>
                                <td>{inst.dueDate}</td>
                                <td>{inst.paid}</td>
                                <td>{inst.receipt}</td>
                                <td>{inst.chargesDue}</td>
                                <td>{inst.chargesPaid}</td>
                                {/* <td>{inst.discount}</td> */}
                              </tr>
                            )) : <tr><td colSpan="8" className="text-center">No records found.</td></tr>}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>

                    {/* Ledger Details */}
                    <Card className="mb-4">
  <CardHeader><h4 className="mb-0">Ledger Details</h4></CardHeader>
  <CardBody>
    <Table bordered striped>
      <thead>
        <tr>
          <th style={{ width: "10%" }}>Date</th>
          <th style={{ width: "20%" }}>Description</th>
          <th style={{ width: "10%" }}>Mode</th>
          <th style={{ width: "30%" }}>Instrument No</th>
          {/* <th style={{ width: "20%" }}>Bank</th> */}
          <th style={{ width: "10%" }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {filteredLedgerDetails.length > 0 ? filteredLedgerDetails.map((ld, idx) => (
          <tr key={idx}>
            <td>{ld.date}</td>
            <td>{ld.desc}</td>
            <td>{ld.mode}</td>
            <td>{ld.instrument}</td>
            {/* <td>{ld.bank}</td> */}
            <td>{ld.amount}</td>
          </tr>
        )) : <tr><td colSpan="6" className="text-center">No ledger records found.</td></tr>}
      </tbody>
    </Table>
  </CardBody>
</Card>


                  </div>
                 </div>
               <style>{`
                .pdf-container th,
                .pdf-container td {
                 border: 1px solid #ccc;
                 padding: 4px;
                 font-size: 7.5px;
                 font-weight: 600;              /* ✅ darker text */
                 color: #000 !important;        /* ✅ force black */
                 text-align: left;
                vertical-align: top;
                word-break: break-word;
                overflow-wrap: break-word;
                white-space: pre-wrap;
                box-sizing: border-box;
                       }


               .pdf-container table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed; 
                }

             .pdf-container th, .pdf-container td {
              border: 1px solid #ccc;
              padding: 4px;
              font-size: 7.5px; /* 👈 Smaller font */
              text-align: left;
              vertical-align: top;
              word-break: break-word;
              overflow-wrap: break-word;
              white-space: pre-wrap;
              box-sizing: border-box;
              }

             .pdf-container th {
              background-color: #f8f8f8;
             }

            .pdf-container tr {
             page-break-inside: avoid !important;
             }

            @media print {
           .pdf-container {
            font-size: 7.5px;
            }
           }

            /* ---- Full-screen statement: branded, readable ---- */
            .fs-statement { font-size: 13px !important; }
            .fs-statement .card { border: none !important; box-shadow: none !important; margin: 0 22px 18px !important; }
            .fs-statement .card:first-child { margin-top: 22px !important; }
            .fs-statement .card-header { background: linear-gradient(90deg,#1f2d4d,#33507e) !important; border: none !important; padding: 12px 18px !important; border-radius: 6px 6px 0 0 !important; }
            .fs-statement .card-header h4 { color: #fff !important; font-size: 15px !important; font-weight: 700 !important; letter-spacing: 0.3px; margin: 0 !important; }
            .fs-statement > .card:first-child .card-header h4 { text-align: center; font-size: 19px !important; }
            .fs-statement th, .fs-statement td { font-size: 12.5px !important; padding: 9px 12px !important; color: #223 !important; border: 1px solid #e4e9f2 !important; }
            .fs-statement thead th { background: #eef2f8 !important; color: #1f2d4d !important; text-transform: uppercase; font-size: 11px !important; letter-spacing: 0.4px; border-bottom: 2px solid #33507e !important; }
            .fs-statement tbody tr:nth-child(even) { background: #f7f9fc !important; }
            .fs-statement tbody tr:hover { background: #eef4ff !important; }
        `}</style>
        
                </>
              )}


               {/* Consents Tab */}
    {!loading && !error && activeTab.id === "consents" && consents.length > 0 && (
      <Card className="mb-4">
        <CardHeader><h4 className="mb-0">Customer Consents</h4></CardHeader>
        <CardBody>
          <Table bordered striped>
            <thead>
              <tr>
                <th>ID</th>
                <th>Consent Type</th>
                <th>Consent Date</th>
                <th>Device ID</th>
                <th>Consented</th>
              </tr>
            </thead>
            <tbody>
              {consents.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>{c.consent_type_id}</td>
                  <td>{c.consent_date}</td>
                  <td>{c.device_id}</td>
                  <td>{c.is_consented ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    )}

    {!loading && !error && activeTab.id === "consents" && consents.length === 0 && (
      <p>No consents found.</p>
    )}



              {/* Agreement / KFS — show ALL versions (Original + Amended), old never removed */}
              {!loading && !error && !["consents", "loan_card_report"].includes(activeTab.id) && docList.length > 0 && (
                <div style={{ textAlign: "left" }}>
                  {docList.map((doc, i) => (
                    <div key={i} style={{ marginBottom: "1.25rem", paddingBottom: "0.75rem",
                        borderBottom: i < docList.length - 1 ? "1px solid #eee" : "none" }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {doc.amended ? "Amended" : "Original"}
                        <span style={{ fontWeight: 400, color: "#888", marginLeft: 8, fontSize: "0.85em" }}>
                          {doc.document_name}
                        </span>
                      </div>
                      <img src={PDF_ICON} alt="PDF Preview"
                        style={{ width: "60px", height: "60px", cursor: "pointer" }}
                        onClick={() => { setDocumentUrl(doc.document_path); openModal(); }} />
                      <p style={{ margin: "4px 0" }}>Click to preview PDF</p>
                      <a href={doc.document_path} download target="_blank" rel="noopener noreferrer">
                        <Button color="primary" size="sm">
                          <i className="fa-solid fa-download mr-1" /> Download
                        </Button>
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* SOA: live detailed Statement of Account (charges / collection / outstanding / ledger) */}
              {!loading && !error && activeTab.id === "statement_of_account" && (
                <PaymentStatementOfAccount />
              )}

              {/* Verification Report: onboarding/KYC checkpoints with pass/fail */}
              {!loading && !error && activeTab.id === "verification_report" && (
                <PaymentVerificationReport />
              )}

              {/* Single-doc tabs (Sanction Letter, NOC, SOA, …) served via documentUrl */}
              {!loading && !error && !["consents", "loan_card_report"].includes(activeTab.id) && docList.length === 0 && documentUrl && (
                <div style={{ textAlign: "left" }}>
  <img
    src={PDF_ICON}
    alt="PDF Preview"
    style={{ width: "60px", height: "60px", cursor: "pointer" }}
    onClick={openModal}
  />
  <p>Click to preview PDF</p>
  <a
    href={documentUrl}
    download
    target="_blank"
    rel="noopener noreferrer"
  >
    <Button color="primary" size="sm">
      <i className="fa-solid fa-download mr-1" /> Download
    </Button>
  </a>
</div>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </>
  );
};

export default Legal;